import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './context/AuthContext';
import Dashboard from './components/Dashboard';
import LoginPage from './components/LoginPage';
import OverviewTab from './components/OverviewTab';
import UserManagement from './components/UserManagement';
import RoleManagement from './components/RoleManagement';
import RepDetails from './components/RepDetails';
import RepDeviceActivation from './components/RepDeviceActivation';
import TargetManagement from './components/TargetManagement';
import SalesRecording from './components/SalesRecording';
import FraudAlerts from './components/FraudAlerts';
import Analytics from './components/Analytics';
import CustomersManagement from './components/CustomersManagement';
import OrdersManagement from './components/OrdersManagement';
import ProductCatalog from './components/ProductCatalog';
import ProductIncentives from './components/ProductIncentives';
import Announcements from './components/Announcements';
import SalaryConfiguration from './components/SalaryConfiguration';
import SalaryProcessing from './components/SalaryProcessing';
import SalarySlips from './components/SalarySlips';
import PaymentTracking from './components/PaymentTracking';
import AttendanceManagement from './components/AttendanceManagement';
import LeaveManagement from './components/LeaveManagement';
import VisitLogsManagement from './components/VisitLogsManagement';
import FuelExpenses from './components/FuelExpenses';
import SystemLogs from './components/SystemLogs';
import Settings from './components/Settings';
import { getRouterBasename } from './utils/routerBasename';
import { ProtectedTab } from './components/ProtectedTab';
import { screenRequiredPrivileges } from './config/roles';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

const GuardedScreen = ({ screen, children }) => (
  <ProtectedTab requiredPrivileges={screenRequiredPrivileges(screen)}>
    {children}
  </ProtectedTab>
);

// Protected Route Wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function App() {
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '442198947942-1ke67oapj512fgfbkoic4sgah3ote82j.apps.googleusercontent.com';
  const routerBasename = getRouterBasename();

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <BrowserRouter basename={routerBasename} key={routerBasename || 'root'}>
          <ErrorBoundary>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            
            <Route path="/" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="/overview" replace />} />
              <Route path="overview" element={<GuardedScreen screen="overview"><OverviewTab /></GuardedScreen>} />
              <Route path="users" element={<GuardedScreen screen="users"><UserManagement /></GuardedScreen>} />
              <Route path="roles" element={<GuardedScreen screen="roles"><RoleManagement /></GuardedScreen>} />
              <Route path="reps" element={<GuardedScreen screen="reps"><RepDetails /></GuardedScreen>} />
              <Route path="device-activation" element={<GuardedScreen screen="devices"><RepDeviceActivation /></GuardedScreen>} />
              <Route path="targets" element={<GuardedScreen screen="targets"><TargetManagement /></GuardedScreen>} />
              <Route path="sales" element={<GuardedScreen screen="sales"><SalesRecording /></GuardedScreen>} />
              <Route path="alerts" element={<GuardedScreen screen="alerts"><FraudAlerts /></GuardedScreen>} />
              <Route path="attendance" element={<GuardedScreen screen="attendance"><AttendanceManagement /></GuardedScreen>} />
              <Route path="leave" element={<GuardedScreen screen="leave"><LeaveManagement /></GuardedScreen>} />
              <Route path="logs" element={<GuardedScreen screen="logs"><VisitLogsManagement /></GuardedScreen>} />
              <Route path="fuel" element={<GuardedScreen screen="fuel"><FuelExpenses /></GuardedScreen>} />
              <Route path="system-logs" element={<GuardedScreen screen="systemLogs"><SystemLogs /></GuardedScreen>} />
              <Route path="analytics" element={<GuardedScreen screen="analytics"><Analytics /></GuardedScreen>} />
              <Route path="customers" element={<GuardedScreen screen="customers"><CustomersManagement /></GuardedScreen>} />
              <Route path="orders" element={<GuardedScreen screen="orders"><OrdersManagement /></GuardedScreen>} />
              <Route path="products" element={<GuardedScreen screen="products"><ProductCatalog /></GuardedScreen>} />
              <Route path="incentives" element={<GuardedScreen screen="incentives"><ProductIncentives /></GuardedScreen>} />
              <Route path="announcements" element={<GuardedScreen screen="announcements"><Announcements /></GuardedScreen>} />
              <Route path="salary-config" element={<GuardedScreen screen="salaryConfig"><SalaryConfiguration /></GuardedScreen>} />
              <Route path="salary-processing" element={<GuardedScreen screen="salaryProcessing"><SalaryProcessing /></GuardedScreen>} />
              <Route path="salary-slips" element={<GuardedScreen screen="salarySlips"><SalarySlips /></GuardedScreen>} />
              <Route path="payment-tracking" element={<GuardedScreen screen="payments"><PaymentTracking /></GuardedScreen>} />
              <Route path="settings" element={<GuardedScreen screen="settings"><Settings /></GuardedScreen>} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </ErrorBoundary>
        </BrowserRouter>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;

